import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, CalendarClock, CalendarDays, CheckCircle2, Loader2, Phone, RotateCcw, Send, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { WhatsAppCallbackButton } from "@/components/common/WhatsAppCallbackButton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";

const STATUS_BADGE = {
  Scheduled: "bg-violet-50 text-violet-700 border-violet-200",
  Confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const CONFIRM_CHIP = {
  response_waiting: { label: "Response waiting", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  issue_flagged: { label: "Issue flagged", cls: "bg-red-50 text-red-700 border-red-200" },
  reschedule_requested: { label: "Reschedule requested", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function RecruiterScheduled() {
  const [tab, setTab] = useState("Scheduled");
  const [data, setData] = useState({ items: [], today: "" });
  const [loading, setLoading] = useState(true);

  const [confirmFor, setConfirmFor] = useState(null); // schedule being confirmed
  const [confirmRole, setConfirmRole] = useState("");
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  // Reschedule = existing cancel ("Rescheduled") + existing schedule-interview, one dialog
  const [reschedFor, setReschedFor] = useState(null);
  const [reschedForm, setReschedForm] = useState({ interview_date: "", company_id: "", role: "" });
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/recruiter-flow/scheduled", { params: { status: tab } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const byDate = new Map();
    for (const s of data.items) {
      if (!byDate.has(s.interview_date)) byDate.set(s.interview_date, []);
      byDate.get(s.interview_date).push(s);
    }
    return [...byDate.entries()]; // already sorted by backend (date asc)
  }, [data.items]);

  const dateChip = (d) => {
    if (!data.today) return null;
    if (d === data.today) return <Badge className="bg-primary text-primary-foreground rounded-full">Today</Badge>;
    if (d < data.today) return <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200">Past date</Badge>;
    return null;
  };

  const submitConfirm = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/recruiter-flow/scheduled/${confirmFor.id}/confirm-going`,
        { role: confirmRole || undefined });
      toast.success(`${confirmFor.candidate_name} marked Going — ${res.data.entry?.going_code || ""}. See the Daily Lineup board.`);
      setConfirmFor(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (cancelReason.trim().length < 3) return toast.error("Add a short reason");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/scheduled/${cancelFor.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Interview cancelled — application closed. To re-engage this candidate, reassign from the Candidate Directory.");
      setCancelFor(null);
      setCancelReason("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openReschedule = (s) => {
    setReschedFor(s);
    setReschedForm({ interview_date: "", company_id: s.company_id || "", role: s.role || "" });
    if (companies.length === 0) {
      api.get("/lineup-board/options")
        .then((r) => setCompanies(r.data.companies || []))
        .catch((err) => toast.error(getErrorMessage(err)));
    }
  };

  // Single action = the existing two-step flow combined:
  // 1) existing cancel with reason "Rescheduled"  2) existing schedule-interview
  const submitReschedule = async () => {
    if (!reschedForm.interview_date) return toast.error("Pick the new interview date");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/scheduled/${reschedFor.id}/cancel`, { reason: "Rescheduled", is_reschedule: true });
      try {
        const res = await api.post(`/recruiter-flow/applications/${reschedFor.application_id}/schedule-interview`, {
          interview_date: reschedForm.interview_date,
          company_id: reschedForm.company_id || reschedFor.company_id,
          role: reschedForm.role || undefined,
          is_reschedule: true,
        });
        toast.success(`Rescheduled to ${formatDate(reschedForm.interview_date)} (${res.data.scheduled?.sched_code || "new entry"})`);
      } catch (err) {
        toast.error(`Old entry was cancelled but rescheduling failed: ${getErrorMessage(err)}. The application is back in your working list — schedule it from there.`);
      }
      setReschedFor(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Scheduled interviews</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Interviews you scheduled, by date. On the interview day, confirm to mark the candidate Going —
            they move to the Daily Lineup board and the company email goes out automatically.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="Scheduled" data-testid="sched-tab-scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="Confirmed" data-testid="sched-tab-confirmed">Confirmed (Going)</TabsTrigger>
          <TabsTrigger value="Cancelled" data-testid="sched-tab-cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : data.items.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={CalendarClock} title={`No ${tab.toLowerCase()} interviews`}
            description={tab === "Scheduled"
              ? "Use “Schedule Interview” on an application to line up the next interview."
              : "Nothing here yet."}
            testId="scheduled-empty" />
        </Card>
      ) : (
        <div className="space-y-6" data-testid="scheduled-list">
          {groups.map(([date, items]) => (
            <div key={date} className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-heading font-semibold" data-testid="scheduled-date-heading">{formatDate(date)}</h2>
                {dateChip(date)}
                <span className="text-xs text-muted-foreground">{items.length} candidate{items.length > 1 ? "s" : ""}</span>
              </div>
              {items.map((s) => (
                <Card key={s.id} className="rounded-xl border-border shadow-none" data-testid="scheduled-item">
                  <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium leading-tight">
                        {s.candidate_name}
                        <span className="font-mono text-[11px] text-muted-foreground ml-2">{s.sched_code}</span>
                        {CONFIRM_CHIP[s.confirm_status] && (
                          <Badge variant="outline" className={`rounded-full ml-2 text-[11px] ${CONFIRM_CHIP[s.confirm_status].cls}`}
                                 data-testid={`confirm-chip-${s.id}`}>
                            {CONFIRM_CHIP[s.confirm_status].label}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{s.company_name}</span>
                        <span>· {s.role}</span>
                        {s.candidate_mobile && (
                          <a href={`tel:${s.candidate_mobile}`} className="inline-flex items-center gap-1 hover:text-foreground">
                            <Phone className="h-3 w-3" /><span className="font-mono">{s.candidate_mobile}</span>
                          </a>
                        )}
                        <Link to={`/recruiter/applications/${s.application_id}`} className="font-mono text-primary hover:underline" data-testid="scheduled-app-link">
                          {s.app_code}
                        </Link>
                      </p>
                      {s.status === "Cancelled" && s.cancel_reason && (
                        <p className="text-[11px] text-muted-foreground mt-1">Cancelled: {s.cancel_reason}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Badge variant="outline" className={`rounded-full ${STATUS_BADGE[s.status] || ""}`} data-testid="scheduled-status-badge">
                        {s.status === "Confirmed" ? "Going" : s.status}
                      </Badge>
                      {s.status === "Scheduled" && (
                        <>
                          <Button size="sm" className="rounded-lg gap-1.5"
                                  onClick={() => { setConfirmFor(s); setConfirmRole(s.role || ""); }}
                                  data-testid={`confirm-going-${s.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Going
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white"
                                  onClick={() => openReschedule(s)}
                                  data-testid={`resched-sched-${s.id}`}>
                            <RotateCcw className="h-3.5 w-3.5" /> Reschedule
                          </Button>
                          <WhatsAppCallbackButton candidateName={s.candidate_name} candidateMobile={s.candidate_mobile} />
                          <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white text-red-600 hover:text-red-700"
                                  onClick={() => { setCancelFor(s); setCancelReason(""); }}
                                  data-testid={`cancel-sched-${s.id}`}>
                            <XCircle className="h-3.5 w-3.5" /> Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Confirm Going dialog */}
      <Dialog open={!!confirmFor} onOpenChange={(o) => !o && setConfirmFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="confirm-going-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm Going</DialogTitle>
            <DialogDescription>
              {confirmFor?.candidate_name} goes on the Daily Lineup board for{" "}
              <span className="font-medium text-foreground">{confirmFor?.company_name}</span> — the grouped
              company email is sent automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Role / profile shown in the email</Label>
              <Input data-testid="confirm-role-input" value={confirmRole}
                     onChange={(e) => setConfirmRole(e.target.value)} placeholder="e.g. Telecaller - Hindi" />
            </div>
            <Button onClick={submitConfirm} disabled={busy} className="w-full rounded-lg" data-testid="confirm-going-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Mark Going
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog — one action: cancel ("Rescheduled") + schedule new */}
      <Dialog open={!!reschedFor} onOpenChange={(o) => !o && setReschedFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="resched-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reschedule — {reschedFor?.candidate_name}</DialogTitle>
            <DialogDescription>
              The current entry is cancelled with reason “Rescheduled” and a new one is created with the
              updated details. Full history is kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>New interview date<span className="text-destructive ml-0.5">*</span></Label>
              <Input type="date" className="bg-white" value={reschedForm.interview_date}
                     onChange={(e) => setReschedForm({ ...reschedForm, interview_date: e.target.value })}
                     data-testid="resched-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Company <span className="text-muted-foreground">(optional change)</span></Label>
              <Select value={reschedForm.company_id} onValueChange={(v) => setReschedForm({ ...reschedForm, company_id: v })}>
                <SelectTrigger className="bg-white" data-testid="resched-company-select"><SelectValue placeholder="Keep current company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Role <span className="text-muted-foreground">(optional change)</span></Label>
              <Input className="bg-white" value={reschedForm.role} placeholder="e.g. Telecaller - Hindi"
                     onChange={(e) => setReschedForm({ ...reschedForm, role: e.target.value })}
                     data-testid="resched-role-input" />
            </div>
            <Button onClick={submitReschedule} disabled={busy} className="w-full rounded-lg" data-testid="resched-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />} Reschedule interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="cancel-sched-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel this interview?</DialogTitle>
            <DialogDescription>
              {cancelFor?.candidate_name}'s application will be <strong>closed</strong> and the candidate returned to the Candidate Directory. To re-engage later, reassign directly from the Candidate Directory. Full history is kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Reason<span className="text-destructive ml-0.5">*</span></Label>
              <Textarea rows={2} data-testid="cancel-reason-input" value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="e.g. Company postponed the drive" />
            </div>
            <Button onClick={submitCancel} disabled={busy} variant="destructive" className="w-full rounded-lg" data-testid="cancel-sched-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Cancel interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
